'use client';

import { Fragment, useCallback, useRef, useState } from 'react';
import Image from 'next/image';
import styles from './WorkShowcase.module.scss';
import { ArrowRight, ChevronLeft, ChevronRight, Phone } from 'lucide-react';
import { gaClick, gaCta } from '@/src/helpers/gtag';
import { useAOS } from '@/src/lib/hooks/useAOS';

export interface WorkItem {
  image: string;
  tag: string;
  title: string;
  fullText: string;
  href?: string;
}

export interface WorkShowcaseProps {
  label?: string;
  title?: string;
  highlightedTitle?: string;
  description?: string;
  disclaimer?: string;
  items?: WorkItem[];
  moreLabel?: string;
  moreHref?: string;
  ctaTitle?: string;
  ctaDescription?: string;
  primaryCta?: string;
  primaryHref?: string;
  secondaryCta?: string;
  secondaryHref?: string;
}

const renderTitle = (text: string) => {
  const parts = text.split('*');
  return parts.map((part, i) => {
    if (i % 2 === 1) {
      return <em key={i}>{part}</em>;
    }
    const segments = part.split('—');
    return segments.map((segment, j) => (
      <Fragment key={j}>
        {segment}
        {j < segments.length - 1 && <span className={styles.titleDash}>—</span>}
      </Fragment>
    ));
  });
};

const renderAccentTitle = (text: string) => {
  const parts = text.split('*');
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <em key={i} className={styles.ctaTitleAccent}>
        {part}
      </em>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    )
  );
};

export const WorkShowcase = ({
  label = 'Use cases',
  title = 'Le type de *livrables* que nous pilotons.',
  highlightedTitle,
  description = 'Quelques exemples concrets de livrables issus de notre expérience en environnement corporate.',
  disclaimer = 'Ces cas sont représentatifs de notre savoir-faire. Les projets réels sont souvent couverts par des accords de confidentialité.',
  items = [],
  moreLabel = 'Voir plus',
  moreHref,
  ctaTitle = 'Votre projet ressemble à l’un de ces cas ?',
  ctaDescription = 'Échangeons 15 minutes pour évaluer vos besoins, sans engagement.',
  primaryCta = 'Prendre rendez-vous',
  primaryHref = '/contact',
  secondaryCta = 'Nous appeler',
  secondaryHref = 'tel:+33649349611',
}: WorkShowcaseProps) => {
  useAOS();
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const scrollToIndex = useCallback(
    (index: number) => {
      const track = trackRef.current;
      if (!track || items.length === 0) {
        return;
      }

      const cards = Array.from(track.querySelectorAll<HTMLElement>('[data-work-card]'));
      if (cards.length === 0) {
        return;
      }

      const nextIndex = (index + cards.length) % cards.length;
      const card = cards[nextIndex];
      const left = card.offsetLeft - track.offsetLeft - (track.clientWidth - card.offsetWidth) / 2;

      track.scrollTo({ left, behavior: 'smooth' });
      setActiveIndex(nextIndex);
    },
    [items.length]
  );

  const scroll = (direction: 'left' | 'right') => {
    if (items.length === 0) {
      return;
    }
    gaClick(`work_showcase_${direction}`);
    scrollToIndex(activeIndex + (direction === 'left' ? -1 : 1));
  };

  const handleTrackScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track) {
      return;
    }

    const cards = Array.from(track.querySelectorAll<HTMLElement>('[data-work-card]'));
    if (cards.length === 0) {
      return;
    }

    const trackCenter = track.scrollLeft + track.clientWidth / 2;
    const nextIndex = cards.reduce((closestIndex, card, index) => {
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const closestCard = cards[closestIndex];
      const closestCenter = closestCard.offsetLeft + closestCard.offsetWidth / 2;

      return Math.abs(cardCenter - trackCenter) < Math.abs(closestCenter - trackCenter)
        ? index
        : closestIndex;
    }, 0);

    setActiveIndex((current) => (current === nextIndex ? current : nextIndex));
  }, []);

  const fullTitle = highlightedTitle ? `${title} *${highlightedTitle}*` : title;

  return (
    <section className={styles.section} aria-labelledby="work-showcase-title">
      <div className={styles.container}>
        <div className={styles.header}>
          {label && <p className={styles.label}>{label}</p>}
          <h2 id="work-showcase-title" className={styles.title}>
            {renderTitle(fullTitle)}
          </h2>
          {description && <p className={styles.description}>{description}</p>}
          {disclaimer && <p className={styles.disclaimer}>{disclaimer}</p>}
        </div>

        <div className={styles.carousel}>
          <button
            type="button"
            className={`${styles.navButton} ${styles.navButtonPrev}`}
            onClick={() => scroll('left')}
            aria-label="Faire défiler vers la gauche"
          >
            <ChevronLeft size={24} />
          </button>

          <div className={styles.track} ref={trackRef} onScroll={handleTrackScroll}>
            {items.map((item, index) => (
              <article key={index} className={styles.card} data-work-card>
                <div className={styles.imageWrapper}>
                  <Image
                    src={item.image}
                    alt={item.title}
                    fill
                    sizes="(max-width: 640px) 85vw, (max-width: 1024px) 50vw, 420px"
                    className={styles.image}
                  />
                  <div className={styles.overlay} aria-hidden="true" />
                </div>

                <span className={styles.tag}>{item.tag}</span>

                <div className={styles.content}>
                  <span className={styles.hoverLine} aria-hidden="true" />
                  <h3 className={styles.cardTitle}>{item.title}</h3>
                  <p className={styles.fullText}>{item.fullText}</p>
                  {item.href && (
                    <a
                      href={item.href}
                      className={styles.cta}
                      onClick={() =>
                        gaClick('work_showcase_card', { title: item.title, href: item.href })
                      }
                    >
                      <span>{moreLabel}</span>
                      <ArrowRight size={16} aria-hidden="true" />
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>

          <button
            type="button"
            className={`${styles.navButton} ${styles.navButtonNext}`}
            onClick={() => scroll('right')}
            aria-label="Faire défiler vers la droite"
          >
            <ChevronRight size={24} />
          </button>

          {items.length > 1 && (
            <div className={styles.mobileControls}>
              <button
                type="button"
                className={styles.mobileNavButton}
                onClick={() => scroll('left')}
                aria-label="Faire défiler vers la gauche"
              >
                <ChevronLeft size={20} />
              </button>
              <div className={styles.mobileDots} aria-label="Choisir une réalisation">
                {items.map((item, index) => (
                  <button
                    key={`${item.title}-${index}`}
                    type="button"
                    className={`${styles.mobileDot} ${
                      index === activeIndex ? styles.mobileDotActive : ''
                    }`}
                    onClick={() => {
                      gaClick('work_showcase_dot', { index });
                      scrollToIndex(index);
                    }}
                    aria-label={`Voir la réalisation ${index + 1}`}
                    aria-current={index === activeIndex ? 'true' : undefined}
                  />
                ))}
              </div>
              <button
                type="button"
                className={styles.mobileNavButton}
                onClick={() => scroll('right')}
                aria-label="Faire défiler vers la droite"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </div>
      </div>

      {(moreHref || primaryHref) && (
        <div className={styles.ctaWrapper}>
          <div className={styles.ctaInner}>
            <h3 className={styles.ctaTitle}>{renderAccentTitle(ctaTitle)}</h3>
            <div className={styles.ctaRight}>
              {ctaDescription && <p className={styles.ctaDescription}>{ctaDescription}</p>}
              <div className={styles.ctaActions}>
                {primaryHref && (
                  <a
                    href={primaryHref}
                    className={styles.primaryCta}
                    onClick={() => gaCta(primaryCta, 'work_showcase_primary', primaryHref)}
                  >
                    <Phone size={16} aria-hidden="true" />
                    {primaryCta}
                  </a>
                )}
                {secondaryHref && (
                  <a
                    href={secondaryHref}
                    className={styles.secondaryCta}
                    onClick={() => gaCta(secondaryCta, 'work_showcase_secondary', secondaryHref)}
                  >
                    {secondaryCta}
                    <ArrowRight size={18} aria-hidden="true" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
